import React, { useMemo, useState } from 'react';
import {
  Box,
  BoxProps,
  Typography,
  IconButton,
  Popover,
  List,
  ListItemButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { ChevronRight, Check as CheckIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReactComponent as CalendarIcon } from '../../../assets/PepWise2.0/Calendar.svg';
import { ReactComponent as CalendarIconActive } from '../../../assets/PepWise2.0/Calendar-Active.svg';
import { ReactComponent as RightArrowBoxIcon } from '../../../assets/PepWise2.0/Right-Arrow-Icon-Box.svg';
import { ReactComponent as LeftArrowBoxIcon } from '../../../assets/PepWise2.0/Left-Arrow-Icon-Box.svg';

dayjs.extend(customParseFormat);

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type PresetOption = {
  label: string;
  value: string;
  showCustomArrow?: boolean;
};

type Props = {
  customMonth: string;
  filterMonth: string;
  setCustomMonth: React.Dispatch<React.SetStateAction<string>>;
  setFilterMonth: React.Dispatch<React.SetStateAction<string>>;
  handleChange: (selectedDate: string, isCustomDate?: boolean) => void;
  boxProps?: BoxProps;
  disableFilter?: boolean;
  disabled?: boolean;
  showTodayOption?: boolean;
  todayLabel?: string;
  showPreviousDayOption?: boolean;
  showThisWeekOption?: boolean;
  showLastWeekOption?: boolean;
  showThisMonthOption?: boolean;
  showLastMonthOption?: boolean;
  showLast3Option?: boolean;
  showLast6Option?: boolean;
  showLast9Option?: boolean;
  showThisOption?: boolean;
  showThisLastOption?: boolean;
  showLast2Option?: boolean;
  showLast3YearsOption?: boolean;
  showCustomOption?: boolean;
  showAllMonthsOption?: boolean;
  showAllMonthsLabel?: boolean;
  isXOps?: boolean;
  isRMCost?: boolean;
};

export default function GenericMonthRangeFilter({
  customMonth,
  filterMonth,
  setCustomMonth,
  setFilterMonth,
  handleChange,
  boxProps,
  disableFilter,
  disabled,
  showTodayOption = false,
  todayLabel,
  showPreviousDayOption = false,
  showThisWeekOption = false,
  showLastWeekOption = false,
  showThisMonthOption = true,
  showLastMonthOption = true,
  showLast3Option = true,
  showLast6Option = true,
  showLast9Option = true,
  showThisOption = true,
  showThisLastOption = true,
  showLast2Option = true,
  showLast3YearsOption,
  showCustomOption = true,
  showAllMonthsOption = true,
  showAllMonthsLabel = true,
  isXOps = false,
  isRMCost = false,
}: Props) {
  const theme = useTheme() as any;
  const colors = theme.palette;
  const dateFilterColors = colors.dateFilter || {};
  const { sx, ...props } = boxProps || {};


  const disabledText =
    dateFilterColors.dayDisabledText ||
    colors.text?.disabled ||
    '#9E9E9E';

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [viewYear, setViewYear] = useState(() => {
    if (/^\d{4}$/.test(filterMonth)) return Number(filterMonth);
    if (customMonth && dayjs(customMonth, 'YYYY-MM', true).isValid()) {
      return dayjs(customMonth, 'YYYY-MM').year();
    }
    return dayjs().year();
  });
  const [showCustom, setShowCustom] = useState(false);
  const [isAllMonths, setIsAllMonths] = useState(/^\d{4}$/.test(filterMonth));

  const isDisabled = disableFilter || disabled;
  const open = Boolean(anchorEl) && !isDisabled;

  const currentYear = dayjs().year();
  const currentMonth = dayjs().month();

  const isFutureMonth = (monthIndex: number) => {
    return viewYear === currentYear && monthIndex > currentMonth;
  };

  const generateXOpsMonths = () => {
    const startDate = dayjs('2025-03', 'YYYY-MM');
    const endDate = dayjs();
    const monthOptions: PresetOption[] = [];
    let currentDate = startDate;

    while (currentDate.isBefore(endDate, 'month') || currentDate.isSame(endDate, 'month')) {
      monthOptions.push({
        value: currentDate.format('YYYY-MM'),
        label: currentDate.format('MMMM YYYY')
      });
      currentDate = currentDate.add(1, 'month');
    }

    return monthOptions;
  };

  const presets = useMemo(() => {
    if (isXOps) {
      return generateXOpsMonths();
    }

    const options: PresetOption[] = [];

    if (showTodayOption) {
      options.push({ label: todayLabel || 'Last 24 Hours', value: '0T' });
    }

    if (showPreviousDayOption) {
      options.push({ label: 'Previous Day', value: '1T' });
    }

    if (showThisWeekOption) {
      options.push({ label: 'This Week', value: '0W' });
    }

    if (showLastWeekOption) {
      options.push({ label: 'Last Week', value: '1W' });
    }

    if (showThisMonthOption) {
      options.push({ label: 'This Month', value: '0M' });
    }

    if (showLastMonthOption) {
      options.push({ label: 'Last Month', value: '1M' });
    }

    if (showLast3Option) {
      options.push({ label: 'Last 3 Months', value: '3M' });
    }

    if (showLast6Option) {
      options.push({ label: 'Last 6 Months', value: '6M' });
    }

    if (showLast9Option) {
      options.push({ label: 'Last 9 Months', value: '9M' });
    }

    if (showThisOption) {
      options.push({ label: 'This Year', value: '0Y' });
    }

    if (showThisLastOption) {
      options.push({ label: 'Last Year', value: '1Y' });
    }

    if (showLast2Option) {
      options.push({ label: 'Last 2 Years', value: '2Y' });
    }

    if (showLast3YearsOption || isRMCost) {
      options.push({ label: 'Last 3 Years', value: '3Y' });
    }

    if (showCustomOption) {
      options.push({ label: 'Custom Month', value: 'customMonth', showCustomArrow: true });
    }

    return options;
  }, [
    isXOps,
    showTodayOption,
    todayLabel,
    showPreviousDayOption,
    showThisWeekOption,
    showLastWeekOption,
    showThisMonthOption,
    showLastMonthOption,
    showLast3Option,
    showLast6Option,
    showLast9Option,
    showThisOption,
    showThisLastOption,
    showLast2Option,
    showLast3YearsOption,
    showCustomOption,
    isRMCost
  ]);

  const selectedMonth = useMemo(() => {
    if (customMonth && dayjs(customMonth, 'YYYY-MM', true).isValid()) {
      return dayjs(customMonth, 'YYYY-MM');
    }

    if (/^\d{4}-\d{2}$/.test(filterMonth)) {
      return dayjs(filterMonth, 'YYYY-MM');
    }

    if (/^\d{4}$/.test(filterMonth)) {
      return dayjs(`${filterMonth}-01-01`);
    }

    if (filterMonth === '0M') return dayjs().startOf('month');
    if (filterMonth === '1M') return dayjs().subtract(1, 'month').startOf('month');
    if (filterMonth === '3M') return dayjs().subtract(3, 'month').startOf('month');
    if (filterMonth === '6M') return dayjs().subtract(6, 'month').startOf('month');
    if (filterMonth === '9M') return dayjs().subtract(9, 'month').startOf('month');
    if (filterMonth === '0Y') return dayjs().startOf('year');
    if (filterMonth === '1Y') return dayjs().subtract(1, 'year').startOf('year');
    if (filterMonth === '2Y') return dayjs().subtract(2, 'year').startOf('year');
    if (filterMonth === '3Y') return dayjs().subtract(3, 'year').startOf('year');

    const parsedFullMonth = dayjs(filterMonth, 'MMMM YYYY', true);
    if (parsedFullMonth.isValid()) {
      return parsedFullMonth.startOf('month');
    }

    const parsedShortMonth = dayjs(filterMonth, 'MMM YYYY', true);
    if (parsedShortMonth.isValid()) {
      return parsedShortMonth.startOf('month');
    }

    return dayjs().startOf('month');
  }, [customMonth, filterMonth]);

  const selectedPreset = useMemo(() => {
    if (/^\d{4}$/.test(filterMonth)) {
      return '';
    }

    if (customMonth) {
      return 'Custom Month';
    }

    if (/^\d{4}-\d{2}$/.test(filterMonth)) {
      const xOpsPreset = presets.find((preset) => preset.value === filterMonth);
      return xOpsPreset?.label || 'Custom Month';
    }

    const selectedOption = presets.find((preset) => preset.value === filterMonth);
    return selectedOption?.label || filterMonth || 'This Month';
  }, [customMonth, filterMonth, presets]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      setAnchorEl(null);
      return;
    }

    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowCustom(false);
  };

  const applyPreset = (preset: PresetOption) => {
    if (preset.value === 'customMonth') {
      setShowCustom(true);
      return;
    }

    setShowCustom(false);
    setIsAllMonths(false);
    setCustomMonth('');
    setFilterMonth(preset.value);
    handleChange(preset.value, false);
    handleClose();
  };

  const handleMonthClick = (monthIndex: number) => {
    if (isFutureMonth(monthIndex)) return;

    const newDate = dayjs().year(viewYear).month(monthIndex).startOf('month');
    const selectedDate = newDate.format('YYYY-MM');
    const formattedDate = newDate.format('MMMM YYYY');

    setIsAllMonths(false);
    setFilterMonth(formattedDate);
    setCustomMonth(selectedDate);
    handleChange(selectedDate, true);
    handleClose();
  };

  const handleAllMonthsToggle = (checked: boolean) => {
    setIsAllMonths(checked);

    if (checked) {
      const selectedYear = String(viewYear);

      setFilterMonth(selectedYear);
      setCustomMonth('');
      handleChange(selectedYear, true);
      handleClose();
      return;
    }

    const fallbackMonthIndex = viewYear === currentYear ? currentMonth : 0;
    const fallbackDate = dayjs().year(viewYear).month(fallbackMonthIndex).startOf('month');
    const selectedDate = fallbackDate.format('YYYY-MM');
    const formattedDate = fallbackDate.format('MMMM YYYY');

    setFilterMonth(formattedDate);
    setCustomMonth(selectedDate);
    handleChange(selectedDate, true);
    handleClose();
  };

  const displayValue = useMemo(() => {
    if (/^\d{4}$/.test(filterMonth)) {
      return showAllMonthsLabel ? `${filterMonth}, All Months` : filterMonth;
    }

    if (isAllMonths) {
      const yearValue = selectedMonth.isValid() ? selectedMonth.format('YYYY') : String(viewYear);
      return showAllMonthsLabel ? `${yearValue}, All Months` : yearValue;
    }

    if (customMonth && selectedMonth.isValid()) {
      return selectedMonth.format('MMM YYYY');
    }

    if (/^\d{4}-\d{2}$/.test(filterMonth)) {
      const selectedOption = presets.find((item) => item.value === filterMonth);
      return selectedOption?.label || selectedMonth.format('MMM YYYY');
    }

    const selectedOption = presets.find((item) => item.value === filterMonth);

    if (selectedOption) {
      return selectedOption.label;
    }

    const parsedFullMonth = dayjs(filterMonth, 'MMMM YYYY', true);
    if (parsedFullMonth.isValid()) {
      return parsedFullMonth.format('MMM YYYY');
    }

    const parsedShortMonth = dayjs(filterMonth, 'MMM YYYY', true);
    if (parsedShortMonth.isValid()) {
      return parsedShortMonth.format('MMM YYYY');
    }

    return selectedMonth.isValid() ? selectedMonth.format('MMM YYYY') : 'This Month';
  }, [
    isAllMonths,
    filterMonth,
    showAllMonthsLabel,
    selectedMonth,
    customMonth,
    presets,
    viewYear
  ]);

  return (
    <Box
      sx={{
        textAlign: 'left',
        height: 'fit-content',
        ...sx
      }}
      {...props}
    >
      <Box
        onClick={isDisabled ? undefined : handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          padding: '8px 16px',
          borderRadius: '4px',
          height: '100%',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.6 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
          backgroundColor: open
            ? `${dateFilterColors.triggerActiveBackground} !important`
            : 'transparent',
          '&:hover': {
            backgroundColor: isDisabled
              ? 'transparent'
              : `${dateFilterColors.triggerHoverBackground} !important`,
          },
        }}
      >
        <Typography
          fontSize="14px"
          sx={{
            color: open
              ? dateFilterColors.triggerTextActive
              : dateFilterColors.triggerText,
            whiteSpace: 'nowrap',
          }}
        >
          {displayValue}
        </Typography>
        {open ? <CalendarIconActive /> : <CalendarIcon />}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              backgroundColor: `${dateFilterColors.popoverBackground} !important`,
              backgroundImage: 'none !important',
              color: `${dateFilterColors.popoverText} !important`,
              borderRadius: '8px',
              boxShadow: dateFilterColors.popoverShadow,
              display: 'flex',
              overflow: 'hidden',
            }
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            background: `${dateFilterColors.popoverBackground} !important`,
          }}
        >
          <Box
            sx={{
              width: 232,
              borderRight: `1px solid ${dateFilterColors.popoverBorder}`,
              background: `${dateFilterColors.presetPanelBackground} !important`,
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
              alignSelf: 'stretch'
            }}
          >
            <List disablePadding sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {presets.map((preset) => (
                <ListItemButton
                  key={preset.value}
                  onClick={() => applyPreset(preset)}
                  selected={selectedPreset === preset.label || filterMonth === preset.value}
                  sx={{
                    fontSize: '14px',
                    height: '44px',
                    fontWeight: 400,
                    color: `${dateFilterColors.presetText} !important`,
                    '&.Mui-selected': {
                      background: `${dateFilterColors.presetSelectedBackground} !important`,
                      color: `${dateFilterColors.presetText} !important`,
                    },
                    '&.Mui-selected:hover': {
                      background: `${dateFilterColors.presetSelectedBackground} !important`,
                      color: `${dateFilterColors.presetText} !important`,
                    },
                    '&:hover': {
                      background: `${dateFilterColors.presetHoverBackground} !important`,
                      color: `${dateFilterColors.presetText} !important`,
                    },
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 'inherit', fontFamily: 'Montserrat', color: 'inherit' }}>
                      {preset.label}
                    </Typography>
                    {preset.showCustomArrow && (
                      <ChevronRight sx={{ fontSize: 18, color: dateFilterColors.presetArrow }} />
                    )}
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Box>

          {showCustom && (
            <Box
              sx={{
                p: 2,
                width: 260,
                background: `${dateFilterColors.calendarBackground} !important`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <IconButton
                  size="small"
                  onClick={() => setViewYear(viewYear - 1)}
                >
                  <RightArrowBoxIcon />
                </IconButton>

                <Typography sx={{ fontWeight: 600, color: dateFilterColors.calendarHeaderText, fontSize: '16px', fontFamily: 'Montserrat' }}>
                  {viewYear}
                </Typography>

                <IconButton
                  size="small"
                  disabled={viewYear >= currentYear}
                  onClick={() => {
                    if (viewYear < currentYear) {
                      setViewYear(viewYear + 1);
                    }
                  }}
                  sx={{
                    opacity: viewYear >= currentYear ? 0.4 : 1,
                    cursor: viewYear >= currentYear ? 'not-allowed' : 'pointer',
                  }}
                >
                  <LeftArrowBoxIcon />
                </IconButton>
              </Box>

              {showAllMonthsOption && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={isAllMonths || /^\d{4}$/.test(filterMonth)}
                        onChange={(e) => handleAllMonthsToggle(e.target.checked)}
                        icon={
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              border: `1.5px solid ${dateFilterColors.triggerText}`,
                              borderRadius: '4px'
                            }}
                          />
                        }
                        checkedIcon={
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              border: `1.5px solid ${dateFilterColors.triggerText} !important`,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <CheckIcon sx={{ color: dateFilterColors.triggerText, fontSize: 18 }} />
                          </Box>
                        }
                        sx={{ padding: '4px 9px' }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px', color: dateFilterColors.triggerText, fontWeight: 500, fontFamily: 'Montserrat' }}>
                        All Months
                      </Typography>
                    }
                  />
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: `1px solid ${dateFilterColors.popoverBorder}` }}>
                {months.map((m, idx) => {
                  const disabledMonth = isFutureMonth(idx);
                  const selectedMonthIsValid = selectedMonth.isValid();
                  const isActive =
                    !disabledMonth &&
                    !isAllMonths &&
                    !/^\d{4}$/.test(filterMonth) &&
                    selectedMonthIsValid &&
                    selectedMonth.year() === viewYear &&
                    selectedMonth.month() === idx;

                  return (
                    <Box
                      key={m}
                      onClick={() => handleMonthClick(idx)}
                      sx={{
                        height: 55,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: disabledMonth ? 'not-allowed' : 'pointer',
                        pointerEvents: disabledMonth ? 'none' : 'auto',
                        fontSize: '14px',
                        color: disabledMonth
                          ? disabledText
                          : dateFilterColors.dayText,
                        bgcolor: isActive ? `${dateFilterColors.rangeBackground} !important` : 'transparent',
                        opacity: disabledMonth ? 0.45 : 1,
                        border: `0.5px solid ${dateFilterColors.popoverBorder}`,
                        fontFamily: 'Montserrat',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: disabledMonth
                            ? 'transparent'
                            : isActive
                              ? `${dateFilterColors.rangeBackground} !important`
                              : `${dateFilterColors.presetHoverBackground} !important`,
                          color: disabledMonth
                            ? disabledText
                            : dateFilterColors.dayText
                        }
                      }}
                    >
                      {m}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}